import { useState } from 'react';
import Button from 'src/components/buttons/button';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">What is a Mediterranean diet?</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">Make your meals more Mediterranean</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">Try one of our heart healthy recipes</a>
        </div>
      </li>
    </ul>
  </section>
);

const getSeason = () => {
  const d = new Date();
  const month = d.getMonth();
  switch (month) {
    case 12:
    case 1:
    case 2:
      return 'WINTER';
    case 3:
    case 4:
    case 5:
      return 'SPRING';
    case 6:
    case 7:
    case 8:
      return 'SUMMER';
    case 9:
    case 10:
    case 11:
      return 'AUTUMN';
  }
};

const getPage = () => {
  const season = getSeason();
  return (
    <div className="navigator">
      <section className="navigator__content">
        <h1 className="navigator__title">{'Week 5: Eating a healthy diet'}</h1>
        <p>
          Everybody should be trying to eat a healthy, balanced diet to help lower their risk of heart and circulatory
          diseases. When you’re living with high cholesterol, this is especially important. Eating well will reduce your risk
          of a heart attack or stroke, as well as reduce your chances of developing other risk factors like Type 2
          diabetes.
        </p>
        <p>
          This week we’ll cover healthy eating, introduce the benefits of a Mediterranean diet and how you can start
          making changes.
        </p>
        <div className="visible-xs">{getMenu()}</div>

        <h2>What is a Mediterranean diet?</h2>
        <p>
          A Mediterranean diet isn’t a fad diet. It’s a way of eating that includes plenty of fruit and vegetables,
          wholegrain cereals, fish (both white and oily), and small amounts of lean meat and low-fat dairy. It also
          includes healthy fats like olive oil and rapeseed oil rather than butter.
        </p>
        <p>
          The health benefits of the Mediterranean diet are not down to any single food, but come from eating the diet
          as a whole.
        </p>
        <p>Research shows that people who eat this way have a lower risk of heart and circulatory diseases.</p>
        <div className="navigator__video-container">
          <iframe
            allow="autoplay; encrypted-media; fullscreen"
            src="https://www.youtube.com/embed/o5aof7UI3yg"
          />
        </div>

        <h2>Make your meals more Mediterranean</h2>
        <section className="navigator__section-half">
          <p>
            You can enjoy many of your favourite foods and meals while eating a Mediterranean diet. Often it just takes
            a few simple swaps.
          </p>
          <p>
            Use our Mediterranean meal swapper to find out what swaps you can make to bring the benefits of healthy
            foods into your everyday routine.
          </p>
        </section>
        <img alt="Minestrone soup" className="navigator__image-half" src={`${PROGRAM_URL}W05B03a.jpg`} />
        <a
          href="https://www.bhf.org.uk/informationsupport/heart-matters-magazine/nutrition/mediterranean-diet/mediterranean-meals-infographic"
          rel="noreferrer"
          target="_blank"
        >
          <Button
            icon="open-right"
            iconLeftAligned={false}
            text="Mediterranean meals"
          />
        </a>

        <div>
          {season === 'WINTER' && (
            <div>
              <h2>Try a tuna burger</h2>
              <section className="navigator__section-half">
                <p>
                  These tuna burgers are a great alternative to meat options, with more omega-3 and fresh vegetables and
                  herbs for added flavor.
                </p>
              </section>
              <img alt="Crab cake" className="navigator__image-half" src={`${PROGRAM_URL}W05B02WINTER.jpg`} />
              <a
                href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/tuna-burgers"
                rel="noreferrer"
                target="_blank"
              >
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
            </div>
          )}
          {season === 'SPRING' && (
            <div>
              <h2>Try piri-piri chicken</h2>
              <section className="navigator__section-half">
                <p>
                  {`This recipe is delicious all year round, but it's always a family favourite when it starts to warm up
                  outside. This version packs a salt-free flavour punch with lots of herbs and spices. `}
                </p>
              </section>
              <img alt="Baked chicken and vegetables" className="navigator__image-half" src={`${PROGRAM_URL}W05B02SPRING.jpg`} />
              <a
                href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/piri-piri-chicken"
                rel="noreferrer"
                target="_blank"
              >
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
            </div>
          )}
          {season === 'SUMMER' && (
            <div>
              <h2>Try 3 bean pasta twist</h2>
              <section className="navigator__section-half">
                <p>
                  Our 3 bean pasta twist is an easy way to get more legumes into your diet, along with some vegetables
                  and a healthy homemade salad dressing. Have it as a side salad or a light lunch.
                </p>
              </section>
              <img alt="Pasta salad" className="navigator__image-half" src={`${PROGRAM_URL}W05B02SUMMER.jpg`} />
              <a
                href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/three-bean-pasta-twist-salad"
                rel="noreferrer"
                target="_blank"
              >
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
            </div>
          )}
          {season === 'AUTUMN' && (
            <div>
              <h2>Try Italian bread soup</h2>
              <section className="navigator__section-half">
                <p>
                  This warming soup is packed with vegetables, healthy fats and fresh herbs. The wholemeal bread makes
                  it filling to keep you going at lunchtime.
                </p>
              </section>
              <img alt="Minestrone soup" className="navigator__image-half" src={`${PROGRAM_URL}W05B02AUTUMN.jpg`} />
              <a
                href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/italian-bread-soup"
                rel="noreferrer"
                target="_blank"
              >
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
            </div>
          )}
        </div>

        <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
      </section>
    </div>
  );
};

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">Join the conversation on HealthUnlocked</div>
        <div className="navigator__footer__description">
          To get the most out of your heart health journey, you’ll need support. Our online community on
          HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice
          and encouragement.{' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
